import { Link } from "react-router-dom";
import { GraduationCap, CalendarCheck, BookOpen, Wallet, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/lib/seed-content";

const BURSAR_POINTS = [
  "Lock the electricity line into your 25-year financial model — exposure is to inflation only, not to volatile wholesale markets.",
  "Free up operational headroom currently absorbed by wholesale-driven tariff swings.",
  "Turn your roof into a 25-year hedge against energy-market volatility, with zero capex impact.",
  "Demonstrate measurable ESG progress to ISI, ESFA, parents and prospective families.",
];

const FEATURES = [
  { icon: CalendarCheck, title: "Term-time aware install", body: "Works scheduled around school holidays, exam periods and inset days. Zero disruption to teaching." },
  { icon: BookOpen, title: "Curriculum-ready data", body: "Live solar generation dashboard for STEM, Geography and Sustainability lessons — bundled at no cost." },
  { icon: Wallet, title: "Operational predictability", body: "A fixed kWh rate for 25 years means the bursary's energy line never moves with wholesale spikes — it's locked in at signing." },
  { icon: ShieldCheck, title: "Governor-friendly contract", body: "Year 9 exit option, transparent agreement and a clear maintenance commitment — built to be reviewed alongside the school's legal counsel." },
];

export default function Schools() {
  return (
    <div data-testid="schools-page">
      {/* HERO */}
      <section className="relative overflow-hidden navy-panel grain" data-testid="schools-hero">
        <div className="container-seed pt-20 pb-24 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <div className="text-overline text-emerald-400">Sector · Schools &amp; Academies</div>
              </FadeUp>
              <FadeUp delay={1}>
                <h1 className="mt-4 font-display font-extrabold text-white tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                  <span className="text-emerald-400">Budget liberation</span><br />for the bursar's office.
                </h1>
              </FadeUp>
              <FadeUp delay={2}>
                <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
                  Operational budget predictability for independent schools and academies. No capex, no
                  disruption — a fixed kWh rate for 25 years that decouples your energy line from
                  volatile wholesale markets.
                </p>
              </FadeUp>
              <FadeUp delay={3}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    data-testid="schools-cta-book"
                    className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-4 px-6 rounded-lg group"
                  >
                    Book a Bursar's Briefing
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                  </Link>
                  <Link
                    to="/trustee-hub"
                    className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 text-white border border-white/15 font-semibold py-4 px-6 rounded-lg"
                  >
                    Governor's resource pack
                  </Link>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={2} className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src={IMAGES.schoolsAerial}
                  alt="Aerial view of school rooftop with solar panels"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="container-seed section-pad" data-testid="schools-why">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-overline">For the bursar</div>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Make energy the most boring line in your budget.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              A school's energy bill should not be the variable that triggers fee uplifts or trims the
              extra-curricular programme. We've designed SEED specifically to take it off your table.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {BURSAR_POINTS.map((b, i) => (
                <FadeUp key={b} delay={i}>
                  <li className="flex gap-4 bg-white border border-slate-200 rounded-xl p-6">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-none">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />
                    </div>
                    <p className="text-slate-700 leading-relaxed text-base">{b}</p>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-slate-100/60 border-y border-slate-200" data-testid="schools-features">
        <div className="container-seed section-pad">
          <div className="text-overline">What's included</div>
          <h2 className="mt-3 font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-3xl">
            Designed for schools, not retro-fitted to schools.
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i}>
                <div className="bg-white border border-slate-200 rounded-xl p-7 h-full">
                  <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center">
                    <f.icon className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-slate-900 text-lg">{f.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{f.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        overline="Schools Programme"
        title="A 25-year energy line, indexed to inflation — not to wholesale shocks."
        subtitle="Share a recent invoice and we'll build a tailored cost projection for your governors' next meeting. Confidential, no obligation."
        ctaLabel="Book Bursar's Briefing"
        testId="schools-cta-section"
      />
    </div>
  );
}
